/**
 * Pure visibility logic for the PWA install prompt (see
 * {@link ../components/InstallPrompt}). No DOM access here — the component
 * collects the environment and this module decides. Kept separate so the
 * whole feature can be deleted cleanly once the native iOS app ships.
 */

/** localStorage key: epoch-ms timestamp until which the prompt stays hidden. */
export const INSTALL_SNOOZE_KEY = "pf_install_snooze";

/** sessionStorage flag: prompt was already shown (or yielded) this session. */
export const INSTALL_SHOWN_SESSION_KEY = "pf_install_shown";

/** How long a dismissal (X, iOS sheet closed, native prompt declined) hides the card. */
export const INSTALL_SNOOZE_DAYS = 30;

/** Delay before the card slides in — never on page load, only once settled. */
export const INSTALL_PROMPT_DELAY_MS = 8000;

/**
 * Snooze value written after a successful install (or `appinstalled`):
 * the max ECMAScript time value, i.e. "never show again".
 */
export const INSTALL_SNOOZE_FOREVER = 8_640_000_000_000_000;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Epoch-ms until which the prompt should stay hidden after a dismissal. */
export function dismissSnoozeUntil(now: number): number {
  return now + INSTALL_SNOOZE_DAYS * DAY_MS;
}

/** Parse the stored snooze timestamp; null when absent or corrupted. */
export function parseSnooze(raw: string | null): number | null {
  if (raw === null || raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * iPhone/iPad UA check. iPadOS 13+ masquerades as "Macintosh", so a Mac UA
 * with a touch screen also counts (real Macs report 0 touch points).
 */
export function isIosUserAgent(ua: string, maxTouchPoints = 0): boolean {
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && maxTouchPoints > 1;
}

export interface InstallPromptEnv {
  /** App already runs installed (display-mode standalone / navigator.standalone). */
  standalone: boolean;
  /** Parsed {@link INSTALL_SNOOZE_KEY} value; null = never snoozed. */
  snoozeUntil: number | null;
  /** {@link INSTALL_SHOWN_SESSION_KEY} was set — shown or yielded this session. */
  shownThisSession: boolean;
  /** A `beforeinstallprompt` event was captured (Android / desktop Chrome). */
  hasInstallEvent: boolean;
  /** Browser supports `beforeinstallprompt` at all (`"onbeforeinstallprompt" in window`). */
  supportsInstallEvent: boolean;
  /** UA is an iPhone/iPad ({@link isIosUserAgent}). */
  iosUserAgent: boolean;
  /** Current epoch-ms. */
  now: number;
}

export type InstallPath = "native" | "ios";

/**
 * Which install flow applies: "native" when a `beforeinstallprompt` event is
 * in hand, "ios" for iPhone/iPad browsers that will never fire one (manual
 * Share → Add to Home Screen), null when this browser can't install at all.
 */
export function getInstallPath(env: InstallPromptEnv): InstallPath | null {
  if (env.hasInstallEvent) return "native";
  if (env.iosUserAgent && !env.supportsInstallEvent) return "ios";
  return null;
}

/**
 * The polite-visibility rule: never when already installed, never twice per
 * session, never while snoozed, and only when an install path actually
 * exists for this browser.
 */
export function shouldShowInstallPrompt(env: InstallPromptEnv): boolean {
  if (env.standalone) return false;
  if (env.shownThisSession) return false;
  if (env.snoozeUntil !== null && env.now < env.snoozeUntil) return false;
  return getInstallPath(env) !== null;
}
